/* =============================================================================
 *  EventLog — terminal-native recent activity feed
 *
 *  Data source: Firestore `customer_activity` collection (same one the
 *  classic `RecentClientActivity` component subscribes to). We consume
 *  the same document shape so any event the classic feed shows, this
 *  does too — no backend change.
 *
 *  Layout: dense monospace log rows in the dashboard right sidebar, under
 *  TodayTimeline. Each row is:
 *     <relative-time>  <type>  <customer>  <title/snippet>
 *  colour-coded by event family (email / job / task / chat).
 *
 *  Click → same open-in-place behaviour as the classic feed:
 *   • email   → EmailPanel viewEmail
 *   • chat    → legacy chat drawer toggle (for now — the terminal chat
 *                lives inside the job workspace; opening from here would
 *                need more wiring)
 *   • job     → JobWorkspaceModal
 *   • task    → JobWorkspaceModal with initialTaskId
 * ============================================================================= */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy,
  query, startAfter, Timestamp,
} from 'firebase/firestore';
import type { QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '../../../../firebase';
// useAndreiChat removed 2026-05 — chat-entity rows no-op.
import { useEmailPanel, PanelEmailData } from '../../mail/contexts/EmailPanelContext';
import JobEditModal from '../../job-summary/JobEditModal';
import JobWorkspaceModal from '../../job-summary/JobWorkspaceModal';
import type { FeedEmailData } from '../../mail/components/EmailFeedContent';

// ── Types ───────────────────────────────────────────────────────────────
type EntityRef = { type: 'email' | 'job' | 'task' | 'chat'; id: string; label?: string };
type ActivityEvent = {
  id: string;
  customerId: string;
  eventType: string;
  primaryEntity: EntityRef;
  relatedEntity?: EntityRef;
  title?: string;
  snippet?: string;
  actor?: string;
  direction?: 'in' | 'out';
  linkUrl?: string;
  timestamp?: Timestamp | Date | { seconds: number; nanoseconds: number } | null;
};

// ── Constants ───────────────────────────────────────────────────────────
const INITIAL_LOAD = 30;
const LOAD_MORE_COUNT = 20;

// ── Helpers ─────────────────────────────────────────────────────────────
// Short tick-style relative — "now", "2m", "3h", "4d". Keeps rows tight.
function formatTickRelative(ts?: ActivityEvent['timestamp']): string {
  try {
    let date: Date | null = null;
    if (!ts) return '';
    if (ts instanceof Timestamp) date = ts.toDate();
    else if (ts instanceof Date) date = ts;
    else if (typeof ts === 'object' && ts && 'seconds' in ts) date = new Date(ts.seconds * 1000);
    if (!date) return '';
    const now = Date.now();
    const diffMs = now - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'now';
    if (diffMin < 60) return `${diffMin}m`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d`;
    const diffWk = Math.floor(diffDay / 7);
    if (diffWk < 5) return `${diffWk}w`;
    return `${Math.floor(diffDay / 30)}mo`;
  } catch {
    return '';
  }
}

// Short event-type label — "email-in", "job", "task", etc.
function typeLabel(et: string): string {
  switch (et) {
    case 'email.received': return 'email-in';
    case 'email.sent':     return 'email-out';
    case 'chat.activity':  return 'chat';
    case 'job.created':    return 'job-new';
    case 'job.status':     return 'job-stat';
    case 'task.created':   return 'task-new';
    case 'task.updated':   return 'task';
    default: {
      // Fallback: shorten unknown verbs
      const parts = et.split('.');
      return parts.length > 1 ? `${parts[0]}-${parts[1].slice(0, 4)}` : et;
    }
  }
}

function typeClass(et: string): string {
  if (et.startsWith('email.')) return 'term-events__type--email';
  if (et.startsWith('job.'))   return 'term-events__type--job';
  if (et.startsWith('task.'))  return 'term-events__type--task';
  if (et.startsWith('chat.'))  return 'term-events__type--chat';
  return '';
}

const EventLog: React.FC = () => {
  const [events, setEvents]       = useState<ActivityEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore]     = useState(true);
  const [customerNameById, setCustomerNameById] = useState<Record<string, string>>({});

  // Chat-entity rows previously opened Andrei from here. Removed
  // entirely 2026-05 — clicking a chat row no-ops now.
  const { openEmailPanel, loadEmailsByIds, preLoadedEmails } = useEmailPanel();

  const lastDocRef = useRef<QueryDocumentSnapshot | null>(null);
  const scrollRef  = useRef<HTMLDivElement>(null);

  // Modal state for job/task entity clicks (mirrors classic RecentClientActivity).
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showJobEditModal, setShowJobEditModal] = useState(false);
  const [initialTaskId, setInitialTaskId] = useState<string | undefined>(undefined);

  // ── Live initial batch (same query + limit as classic feed) ──────────
  useEffect(() => {
    setIsLoading(true);
    setEvents([]);
    lastDocRef.current = null;
    setHasMore(true);

    const q = query(
      collection(db, 'customer_activity'),
      orderBy('timestamp', 'desc'),
      limit(INITIAL_LOAD),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows: ActivityEvent[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        setEvents(rows);
        lastDocRef.current = snap.docs[snap.docs.length - 1] || null;
        setHasMore(snap.docs.length === INITIAL_LOAD);
        setIsLoading(false);
      },
      () => setIsLoading(false),
    );
    return () => unsub();
  }, []);

  // ── Customer-name hydration (cached; same pattern as classic) ────────
  useEffect(() => {
    const uniqueIds = Array.from(new Set(events.map((e) => e.customerId).filter(Boolean)));
    const missing = uniqueIds.filter((id) => !customerNameById[id]);
    if (missing.length === 0) return;

    let cancelled = false;
    (async () => {
      const updates: Record<string, string> = {};
      await Promise.all(missing.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, 'customers', id));
          if (snap.exists()) {
            const data = snap.data() as { name?: string; company?: string };
            // Prefer company for the compact sidebar; fall back to name.
            const display = data?.company || data?.name || id;
            updates[id] = display;
          } else {
            updates[id] = id;
          }
        } catch {
          updates[id] = id;
        }
      }));
      if (!cancelled && Object.keys(updates).length > 0) {
        setCustomerNameById((prev) => ({ ...prev, ...updates }));
      }
    })();
    return () => { cancelled = true; };
  }, [events, customerNameById]);

  // ── Load more (pagination) ───────────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !lastDocRef.current) return;
    setIsLoadingMore(true);
    try {
      const q = query(
        collection(db, 'customer_activity'),
        orderBy('timestamp', 'desc'),
        startAfter(lastDocRef.current),
        limit(LOAD_MORE_COUNT),
      );
      const snap = await getDocs(q);
      const newRows: ActivityEvent[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      if (newRows.length > 0) {
        setEvents((prev) => {
          const existingIds = new Set(prev.map((e) => e.id));
          return [...prev, ...newRows.filter((e) => !existingIds.has(e.id))];
        });
        lastDocRef.current = snap.docs[snap.docs.length - 1] || lastDocRef.current;
      }
      setHasMore(snap.docs.length === LOAD_MORE_COUNT);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[EventLog] loadMore error:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore]);

  // Infinite scroll near the bottom of the log container.
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || isLoadingMore || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      void loadMore();
    }
  }, [isLoadingMore, hasMore, loadMore]);

  // ── Row click handlers (mirrors classic RecentClientActivity) ────────
  const handleOpenEmail = async (id: string) => {
    try {
      const existing = (preLoadedEmails as FeedEmailData[]).find((em: any) => {
        const x = String(em?.id || em?.emailId || '');
        return x === id;
      });
      if (existing) {
        openEmailPanel('viewEmail', existing as any as PanelEmailData);
        return;
      }
      const loaded = await loadEmailsByIds([id]);
      if (loaded?.[0]) {
        openEmailPanel('viewEmail', loaded[0] as any as PanelEmailData);
        return;
      }
      // Fallback: direct Firestore read
      const snap = await getDoc(doc(db, 'emails', id));
      if (snap.exists()) {
        openEmailPanel('viewEmail', ({ id: snap.id, ...(snap.data() as any) }) as any as PanelEmailData);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[EventLog] handleOpenEmail error:', err);
    }
  };

  const handleOpenTaskOrJob = async (ev: ActivityEvent) => {
    try {
      if (ev.primaryEntity?.type === 'task' && ev.primaryEntity.id) {
        const taskSnap = await getDoc(doc(db, 'tasks', ev.primaryEntity.id));
        const tdata = taskSnap.exists() ? (taskSnap.data() as any) : null;
        const jobId = tdata?.jobId;
        if (jobId) {
          const jobSnap = await getDoc(doc(db, 'jobs', jobId));
          if (jobSnap.exists()) {
            const jd = jobSnap.data() as any;
            setSelectedJob({
              id: jobSnap.id,
              jobLabel: jd.jobLabel || 'Job',
              description: jd.description || '',
              customerId: Array.isArray(jd.customerId) ? jd.customerId : (jd.customerId ? [jd.customerId] : []),
              unbilledHours: jd.unbilledHours ?? 0,
              totalHours: jd.totalHours ?? 0,
              creationDate: jd.creationDate || '',
              dueDate: jd.dueDate || '',
              status: jd.status || 'Open',
              lastEdited: jd.lastEdited || new Date().toISOString(),
            });
            setInitialTaskId(ev.primaryEntity.id);
            setShowTaskModal(true);
            return;
          }
        }
      }
      if (ev.primaryEntity?.type === 'job' && ev.primaryEntity.id) {
        const jobSnap = await getDoc(doc(db, 'jobs', ev.primaryEntity.id));
        if (jobSnap.exists()) {
          const jd = jobSnap.data() as any;
          setSelectedJob({
            id: jobSnap.id,
            jobLabel: jd.jobLabel || 'Job',
            description: jd.description || '',
            customerId: Array.isArray(jd.customerId) ? jd.customerId : (jd.customerId ? [jd.customerId] : []),
            unbilledHours: jd.unbilledHours ?? 0,
            totalHours: jd.totalHours ?? 0,
            creationDate: jd.creationDate || '',
            dueDate: jd.dueDate || '',
            status: jd.status || 'Open',
            lastEdited: jd.lastEdited || new Date().toISOString(),
          });
          setShowJobEditModal(true);
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[EventLog] handleOpenTaskOrJob error:', err);
    }
  };

  // Chat-entity activity rows are no longer interactive (2026-05).
  // The legacy GeminiChatInterface that displayed them is gone, and
  // there's no useful target for the click.
  const onRowClick = (ev: ActivityEvent) => {
    const t = ev.primaryEntity?.type;
    const id = ev.primaryEntity?.id;
    if (!t || !id) return;
    if (t === 'email') return void handleOpenEmail(id);
    if (t === 'task' || t === 'job') return void handleOpenTaskOrJob(ev);
    // Other types (incl. 'chat'): no-op
  };

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <section className="term-events" aria-label="Recent activity">
      <header className="term-events__head">
        <span className="term-events__title">── event log ──</span>
        {!isLoading && events.length > 0 && (
          <span className="term-events__count">{events.length}{hasMore ? '+' : ''}</span>
        )}
      </header>

      <div
        ref={scrollRef}
        className="term-events__scroll"
        onScroll={handleScroll}
      >
        {isLoading ? (
          <div className="term-events__loading">loading activity…</div>
        ) : events.length === 0 ? (
          <div className="term-events__empty">no recent activity</div>
        ) : (
          <ul className="term-events__list">
            {events.map((ev) => {
              const custName = customerNameById[ev.customerId] || ev.customerId || '—';
              const when = formatTickRelative(ev.timestamp);
              const label = typeLabel(ev.eventType || '');
              const subject = (ev.title || ev.snippet || '').toString().trim();
              return (
                <li
                  key={ev.id}
                  className="term-events__row"
                  onClick={() => onRowClick(ev)}
                  title={`${label} · ${custName}${subject ? ' · ' + subject : ''}`}
                >
                  <span className="term-events__when">{when}</span>
                  <span className={`term-events__type ${typeClass(ev.eventType || '')}`}>{label}</span>
                  <span className="term-events__cust">{custName}</span>
                  {subject && (
                    <>
                      <span className="term-events__sep">·</span>
                      <span className="term-events__subject">{subject}</span>
                    </>
                  )}
                </li>
              );
            })}
            {isLoadingMore && (
              <li className="term-events__loading-more">… loading more</li>
            )}
            {!hasMore && events.length > INITIAL_LOAD && (
              <li className="term-events__end">— end of log —</li>
            )}
          </ul>
        )}
      </div>

      {/* Task/job modals — opened in place, same as classic feed */}
      {selectedJob && (
        <>
          <JobWorkspaceModal
            job={selectedJob}
            show={showTaskModal}
            onHide={() => { setShowTaskModal(false); setSelectedJob(null); setInitialTaskId(undefined); }}
          />
          <JobEditModal
            show={showJobEditModal}
            job={selectedJob}
            onHide={() => { setShowJobEditModal(false); setSelectedJob(null); }}
          />
        </>
      )}
    </section>
  );
};

export default EventLog;
